/**
 * Full itinerary list view — trip header + all days and events laid out
 * sequentially. Rendering of the day/transit blocks themselves lives in
 * TravelItinerarySection so the summary view can reuse it.
 */

import type {
  TravelAttachmentResponse,
  TravelEventResponse,
  TravelItemResponse,
  TravelTripResponse,
} from '../api';
import { TravelItinerarySection } from './TravelItinerarySection';

interface TravelListViewProps {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  attachments?: TravelAttachmentResponse[];
  /** When true, edit affordances (if any) are suppressed. The list view is
   * almost entirely display-only already, but this keeps the API consistent
   * with the other views and lets future edits stay guarded. */
  readOnly?: boolean;
}

export function TravelListView({
  trip,
  items,
  events,
  attachments = [],
  readOnly: _readOnly = false,
}: TravelListViewProps) {
  const dayCount = items.filter((i) => i.itemType === 'day').length;
  const totalEvents = events.length;
  const allCities = new Set<string>();
  const allCountries = new Set<string>();
  items.forEach((i) => {
    if (i.city) allCities.add(i.city);
    if (i.country) allCountries.add(i.country);
    if (i.locations) {
      for (const loc of i.locations) {
        if (loc.city) allCities.add(loc.city);
        if (loc.country) allCountries.add(loc.country);
      }
    }
  });

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8 pb-6 border-b border-border">
        <h1 className="text-3xl font-bold tracking-tight">{trip.title}</h1>
        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-muted-foreground">
          <span>{dayCount} days</span>
          <span>{totalEvents} events</span>
          {allCities.size > 0 && <span>{allCities.size} cities</span>}
          {allCountries.size > 0 && <span>{allCountries.size} countries</span>}
        </div>
      </div>

      <TravelItinerarySection trip={trip} items={items} events={events} attachments={attachments} />
    </div>
  );
}
